// @flow
/* eslint-disable no-console */

import diff from 'deep-diff';
import { flatten, get, isEmpty, isEqual, omit } from 'lodash';

import { timer } from '@zapier/common-utils';

import getElementKey from './getElementKey';

export function deepCheckStateAndProps(nextProps: {}, nextState: {}): boolean {
  return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
}

export function deepCheckProps(nextProps: {}): boolean {
  return !isEqual(this.props, nextProps);
}

const log = (...args) => console.info.apply(console, args);

// DO NOT USE THIS
//
// This should only be done to get a quick
// measurement on a possible performance improvement
const DANGEROUSLY_ENABLE_AUTO_OPTIMIZE = false;

const logDiffs = (curr, next, diffs = []) =>
  diffs.forEach(delta => {
    const path = flatten([delta.path]).join('.');
    log(`${path}`);
    log(`  Before:`, get(curr, path));
    log(`  After: `, get(next, path));
  });

export function explainComponentUpdate(
  nextProps: {},
  nextState: ?{},
  nextContext: ?{}
): boolean {
  const componentName = this.constructor.displayName || this.constructor.name;
  const elementKey = getElementKey(this);
  const timerName = '🔥 Explaining these updates';
  timer.start(timerName);
  console.group(`${componentName}${elementKey}.shouldComponentUpdate`);

  const arePropsDeeplyEqual = isEqual(this.props, nextProps);
  const arePropsShallowEqual =
    isEqual(Object.keys(this.props), Object.keys(nextProps)) &&
    Object.keys(this.props).every(key => this.props[key] === nextProps[key]);
  const areStateDeeplyEqual = isEqual(this.state, nextState);
  const areContextsDeeplyEqual = isEqual(this.context, nextContext);

  try {
    if (
      DANGEROUSLY_ENABLE_AUTO_OPTIMIZE &&
      arePropsDeeplyEqual &&
      areStateDeeplyEqual
    ) {
      console.info(`optimized update for ${componentName}`);
      return false;
    }

    if (
      arePropsDeeplyEqual &&
      !arePropsShallowEqual &&
      this.props !== nextProps
    ) {
      console.group(
        `🚨 ${componentName} props changed by reference but not by value`
      );
      log('top level props with different references:');
      Object.keys(this.props).forEach(key => {
        if (this.props[key] !== nextProps[key]) {
          log(`  ${key}`);
        }
      });
      console.groupEnd();
    } else if (this.props !== nextProps && !arePropsDeeplyEqual) {
      const propsDiff = diff(
        omit(this.props, 'children'),
        omit(nextProps, 'children')
      );
      console.group(
        `🥺 ${componentName} props changed by reference and by value`
      );
      logDiffs(this.props, nextProps, propsDiff);
      if (isEmpty(propsDiff)) {
        log('unable to identify the change', this.props, nextProps);
      }
      console.groupEnd();
    } else if (this.props === nextProps && !arePropsDeeplyEqual) {
      const propsDiff = diff(
        omit(this.props, 'children'),
        omit(nextProps, 'children')
      );
      console.group(
        `🔥 ${componentName} props changed by value but not by reference`
      );
      logDiffs(this.props, nextProps, propsDiff);
      console.groupEnd();
    }

    if (areStateDeeplyEqual && this.state !== nextState) {
      log(`🚨 ${componentName} state changed by reference but not by value`);
    } else if (this.state !== nextState && !areStateDeeplyEqual) {
      const stateDiff = diff(this.state, nextState);
      console.group(
        `🥺 ${componentName} state changed by reference and by value`
      );
      logDiffs(this.state, nextState, stateDiff);
      console.groupEnd();
    }

    if (areContextsDeeplyEqual && this.context !== nextContext) {
      log(`🚨 ${componentName} context changed by reference but not by value`);
    } else if (this.context !== nextContext && !areContextsDeeplyEqual) {
      const contextDiffs = diff(this.context, nextContext);
      console.group(
        `🥺 ${componentName} context changed by reference and by value`
      );
      logDiffs(this.context, nextContext, contextDiffs);
      console.groupEnd();
    }

    return true;
  } finally {
    timer.stop(timerName);
    console.groupEnd();
  }
}

type PerformanceGuardPolicy = 'throw' | 'error';

export const handleReferenceOnlyChange = (policy: PerformanceGuardPolicy) =>
  function onReferenceOnlyChange(nextProps: {}): boolean {
    const arePropsDeeplyEqual = isEqual(this.props, nextProps);
    if (arePropsDeeplyEqual && this.props !== nextProps) {
      const problems = Object.keys(this.props).filter(
        key => this.props[key] !== nextProps[key]
      );
      const problem = problems.join(', ');
      const message = `The following props changed by reference only: ${problem}. This degrades performance due to needless re-rendering.`;

      if (policy === 'throw') {
        throw new Error(message);
      } else {
        console.error(message);
      }
    }
    return true;
  };

export const errorOnReferenceOnlyChange = handleReferenceOnlyChange('error');
export const throwOnReferenceOnlyChange = handleReferenceOnlyChange('throw');

// DEPRECATED remove these and all uses in favor of direct exports
export default {
  deepCheckStateAndProps,
  deepCheckProps,
};
